import React, { useEffect, useState } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  browserPopupRedirectResolver,
} from "firebase/auth";
import "../styles/SignIn.css";
import "../mobile_styles/SignIn.css";
import { HiChevronDown, HiUser, HiUserCircle, HiLogout } from "react-icons/hi";
import { useDineCentralAuth } from "../hooks/useDineCentralAuth";
import { DINECENTRAL_AUTH } from "../Firebase.dinecentral";
import { FIREBASE_AUTH } from "../Firebase";
import ToastService from "../service/ToastService";
import { useTheme } from "../hooks/ThemeContext";
import { useNavigate } from "react-router-dom";

type Props = {
  setActiveUserId: any;
  accountUrl?: string;
};

// ── Kept exactly as before — used by LoginNew.tsx and other store auth flows ──

export const processFirebaseError = (error: string) => {
  let processed = error.split("/")[1].split(")")[0].replaceAll("-", " ");
  let output = processed.charAt(0).toUpperCase() + processed.slice(1);
  return output;
};

export const SignInWithGoogle = (redirectUrl: string) => {
  console.log("SignInWithGoogle called with redirectUrl:", redirectUrl);

  if (!FIREBASE_AUTH) {
    return Promise.resolve("ERROR: Firebase Auth not initialized");
  }

  const provider = new GoogleAuthProvider();
  provider.addScope("profile");
  provider.addScope("email");

  return signInWithPopup(FIREBASE_AUTH, provider, browserPopupRedirectResolver)
    .then((result) => {
      console.log("Google Sign In successful:", result);
      return "SUCCESS";
    })
    .catch((error) => {
      console.error("Google Sign In error:", error);
      return "ERROR: " + processFirebaseError(error.message);
    });
};

export const SignInWithEmail = (email: string, password: string, redirectUrl = "") => {
  const auth = getAuth();
  return signInWithEmailAndPassword(auth, email, password)
    .then((result) => {
      const user = result.user;
      if (user.emailVerified) {
        if (redirectUrl !== "") {
          window.self.close();
        }
        return "SUCCESS";
      } else {
        return "NOT_VERIFIED";
      }
    })
    .catch((error) => {
      return "ERROR: " + processFirebaseError(error.message);
    });
};

export const SignUpWithEmail = (email: string, password: string, name: string) => {
  const auth = getAuth();
  return createUserWithEmailAndPassword(auth, email, password)
    .then(async (result) => {
      const user = result.user;
      if (user) {
        try {
          await updateProfile(user, { displayName: name });
          return "SUCCESS";
        } catch (error) {
          console.error("Error updating user profile:", error);
          return "ERROR: Failed to update user profile";
        }
      } else {
        return "USER_NOT_LOADED";
      }
    })
    .catch((error) => {
      console.log(error.message);
      return "ERROR: " + processFirebaseError(error.message);
    });
};

export const SignOut = (redirect?: string) => {
  const auth = getAuth();
  signOut(auth);
  ToastService.showToast("Logged Out", {
    style: {
      backgroundColor: "darkred",
      color: "white",
      minHeight: "80px",
      borderRadius: "15px",
    },
  });
  window.location.href = redirect || "/login";
};

// ── SignIn UI component — now uses DineCentral auth for customer-facing areas ──

export const SignIn = (props: Props) => {
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const { user } = useDineCentralAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close when tapping/clicking outside (works on touch and desktop).
  useEffect(() => {
    if (!showProfileMenu) return;
    const onOutside = (e: any) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    if (user) {
      props.setActiveUserId(user.uid || "");
    } else {
      props.setActiveUserId("");
    }
  }, [user]);

  const handleSignOut = () => {
    signOut(DINECENTRAL_AUTH);
    ToastService.showToast("Logged Out", {
      style: {
        backgroundColor: "darkred",
        color: "white",
        minHeight: "80px",
        borderRadius: "15px",
      },
    });
    navigate("/");
  };

  const handleLoginClick = () => navigate("/login");
  const handleRegisterClick = () => navigate("/login");
  const handleAccountClick = () => {
    setShowProfileMenu(false);
    navigate(props.accountUrl || "/account");
  };

  return (
    <div
      className="auth-button"
      ref={menuRef}
      style={{ zIndex: "100000000", fontSize: "x-large", height: "100%", color: theme.topbarIconColor }}
    >
      <span
        className="user-icon"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={showProfileMenu}
        onClick={() => setShowProfileMenu((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowProfileMenu((v) => !v); }
          if (e.key === "Escape") setShowProfileMenu(false);
        }}
      >
        <HiUser /> <HiChevronDown />
      </span>

      {showProfileMenu && (
        <div
          className="dropdown-menu"
          style={{
            backgroundColor: theme.cardColor || "#ffffff",
            color: theme.textColor || "#1a1a1a",
            ["--dd-border" as any]: theme.borderColor || "#e5e5e5",
            ["--dd-accent" as any]:
              theme.categoryIndicatorColor || theme.topbarIconColor || "#1a1a1a",
          }}
        >
          {!user && (
            <>
              <div className="dropdown-menu-item" onClick={handleLoginClick}>
                <HiUser /> Login
              </div>
              <div className="dropdown-menu-item" onClick={handleRegisterClick}>
                <HiUserCircle /> Register
              </div>
            </>
          )}
          {user && (
            <>
              <div className="dropdown-header">
                <span className="dropdown-header-label">Signed in as</span>
                <span className="dropdown-email">{user.email}</span>
              </div>
              <div className="dropdown-divider" />
              <div className="dropdown-menu-item" onClick={handleAccountClick}>
                <HiUserCircle /> Account
              </div>
              <div className="dropdown-menu-item danger" onClick={handleSignOut}>
                <HiLogout /> Log Out
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SignIn;